import React, { useState } from 'react';
import { Mail, FileText, Share2, Instagram, Facebook, Linkedin } from 'lucide-react';

interface ActivityItem {
  id: string;
  type: 'blog' | 'campaign' | 'social';
  title: string;
  date: Date;
  status: string;
  platform?: string;
  imageUrl?: string;
}

interface ActivityMonthViewProps {
  currentDate: Date;
  items: ActivityItem[];
  onDayClick: (date: Date) => void;
}

const TYPE_CONFIG = {
  blog: { icon: FileText, gradient: 'from-emerald-500 to-teal-600', dotColor: 'bg-emerald-500' },
  campaign: { icon: Mail, gradient: 'from-sky-500 to-blue-600', dotColor: 'bg-sky-500' },
  social: { icon: Share2, gradient: 'from-amber-400 to-orange-500', dotColor: 'bg-amber-500' },
};

function getPlatformIcon(platform?: string) {
  if (platform?.toLowerCase().includes('instagram')) return Instagram;
  if (platform?.toLowerCase().includes('facebook')) return Facebook;
  if (platform?.toLowerCase().includes('linkedin')) return Linkedin;
  return Share2;
}

function getHeatmapColor(count: number): string {
  if (count === 0) return '';
  if (count === 1) return 'bg-emerald-50';
  if (count === 2) return 'bg-emerald-100/70';
  if (count === 3) return 'bg-emerald-100';
  return 'bg-emerald-200/70';
}

export function ActivityMonthView({ currentDate, items, onDayClick }: ActivityMonthViewProps) {
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const startDay = firstDay.getDay();

  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - (startDay === 0 ? 6 : startDay - 1));

  const weeks: Date[][] = [];
  let currentWeek: Date[] = [];
  const tempDate = new Date(startDate);

  for (let i = 0; i < 42; i++) {
    currentWeek.push(new Date(tempDate));
    tempDate.setDate(tempDate.getDate() + 1);

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  const getItemsForDate = (date: Date) => {
    return items.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate.getDate() === date.getDate() &&
             itemDate.getMonth() === date.getMonth() &&
             itemDate.getFullYear() === date.getFullYear();
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === month;
  };

  const getDayKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="grid grid-cols-7 gap-px bg-gray-100">
        {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => (
          <div
            key={day}
            className="bg-gray-50/50 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-gray-100">
        {weeks.map((week, weekIdx) => (
          <React.Fragment key={weekIdx}>
            {week.map((date, dayIdx) => {
              const dayItems = getItemsForDate(date);
              const dayKey = getDayKey(date);
              const isHovered = hoveredDay === dayKey;
              const heatmap = getHeatmapColor(dayItems.length);

              return (
                <div
                  key={dayIdx}
                  className={`relative bg-white min-h-[100px] p-2 transition-colors ${heatmap} ${
                    !isCurrentMonth(date) ? 'opacity-30' : ''
                  } ${isCurrentMonth(date) && dayItems.length > 0 ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  onMouseEnter={() => dayItems.length > 0 ? setHoveredDay(dayKey) : null}
                  onMouseLeave={() => setHoveredDay(null)}
                  onClick={() => isCurrentMonth(date) && dayItems.length > 0 ? onDayClick(date) : null}
                >
                  <div className={`text-sm font-medium mb-2 ${
                    isToday(date)
                      ? 'w-7 h-7 rounded-full bg-[#FF6B35] text-white flex items-center justify-center'
                      : 'text-gray-900'
                  }`}>
                    {date.getDate()}
                  </div>

                  {dayItems.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {dayItems.map((item) => (
                        <div
                          key={item.id}
                          className={`w-2.5 h-2.5 rounded-full ${TYPE_CONFIG[item.type].dotColor}`}
                        />
                      ))}
                    </div>
                  )}

                  {isHovered && isCurrentMonth(date) && dayItems.length > 0 && (
                    <div className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 p-3 pointer-events-none">
                      <div className="space-y-2">
                        {dayItems.map((item) => {
                          const typeConfig = TYPE_CONFIG[item.type];
                          const FallbackIcon = item.type === 'social' ? getPlatformIcon(item.platform) : typeConfig.icon;

                          return (
                            <div key={item.id} className="flex items-center gap-2.5">
                              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                                {item.imageUrl ? (
                                  <img
                                    src={item.imageUrl}
                                    alt={item.title}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className={`w-full h-full bg-gradient-to-br ${typeConfig.gradient} flex items-center justify-center`}>
                                    <FallbackIcon className="w-4 h-4 text-white/40" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate">
                                  {item.title}
                                </p>
                                <p className="text-[11px] text-gray-400">
                                  {item.date.toLocaleTimeString('nl-NL', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-100 rotate-45" />
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
